import React from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import Status from 'Components/DesignSystem/Status/Status';

// Constants and i18n
import { STRINGS } from '../../constants';
import { formatTimeOfDay, interpolate } from '../../i18n';

// Types
import { SaveStatus } from '../../hooks';

interface SaveIndicatorProps {
  status: SaveStatus;
  /** ISO-8601 time of the last successful write. */
  lastSavedAt: string | null;
  onRetry: () => void;
  /**
   * True when the failure was the server refusing an input (a duplicate card
   * name), rather than the write not landing. Retry is withheld: the same
   * request fails identically, so offering the button promises a recovery that
   * cannot happen and hides the one action that works — editing the field. The
   * alert below the header carries the server's own sentence saying so.
   */
  isInputRefusal?: boolean;
}

/**
 * The editor's save indicator.
 *
 * The module has no Save control by design (spec R8, R45). That is the right
 * interaction and it removes the user's evidence: with no button to press and
 * no receipt afterwards, a working autosave and a broken one presented
 * identically. Before this component the only feedback in the entire feature
 * was a red banner on failure.
 *
 * ACCESSIBILITY
 * -------------
 * `role="status"` (an implicit `aria-live="polite"`) rather than `role="alert"`.
 * Assertive announcements interrupt whatever a screen reader is currently
 * reading, and this text changes on every debounce tick — a polite region waits
 * for a pause. The failure case is different: it is the one the user must act
 * on, so it carries `role="alert"` and an actual control.
 *
 * A colour is never the only cue (WCAG 1.4.1): every state carries its own
 * words, and the failure state carries a button as well.
 */
const SaveIndicator = ({
  status,
  lastSavedAt,
  onRetry,
  isInputRefusal = false,
}: SaveIndicatorProps) => {
  if (status === 'error') {
    return (
      <div
        role="alert"
        className="tw-flex tw-items-center tw-gap-2 tw-shrink-0"
      >
        {/* Same words either way — "Not saved" is true of both. What changes is
            whether a retry is offered. */}
        <Status tone="danger" label={STRINGS.editor.saveStatus.error} />
        {!isInputRefusal && (
          <Button variant="tertiary" showIcon={false} onClick={onRetry}>
            {STRINGS.editor.saveRetry}
          </Button>
        )}
      </div>
    );
  }

  // Nothing has been written yet this session. Saying "All changes saved"
  // before anything has been saved would be a claim we cannot back.
  if (status === 'idle' && lastSavedAt === null) {
    return null;
  }

  const message =
    status === 'saving'
      ? STRINGS.editor.saveStatus.saving
      : lastSavedAt === null
        ? STRINGS.editor.saveStatus.saved
        : interpolate(STRINGS.editor.saveStatus.savedAt, {
            time: formatTimeOfDay(lastSavedAt),
          });

  return (
    <div
      role="status"
      aria-label={STRINGS.editor.saveRegionLabel}
      className="tw-shrink-0"
    >
      <Status
        tone={status === 'saving' ? 'info' : 'success'}
        label={message}
        pulse={status === 'saving'}
      />
    </div>
  );
};

export default SaveIndicator;
